import { useUser } from './useUser/useUser';
import zipCodes from '~/helpers/validation/zipCodesValidationRules.json';
import { useCart } from '~/composables/custom/useCart/useCart';
import useUiNotification from '~/composables/useUiNotification';
import { useTracking } from '~/composables/custom/useTracking';
import { useBFF } from '~/composables/useBFF/useBFF';
import type { Address, Maybe, ShippingMethod } from '~/types/GraphQL';

const updating = ref(false);

/**
 * Mainly used for parsing zip code provided by StoreFinder
 * @param country string
 * @param postalCode string
 * @returns string
 */
const parseZipCode = (country?: string, postalCode?: Maybe<string>) => {
  if (!postalCode || !country) {
    return postalCode;
  }

  switch (country.toLocaleLowerCase()) {
    case 'cz': {
      if (RegExp(zipCodes['CZ']).test(postalCode)) {
        return postalCode;
      }

      const trimmed = postalCode.trim().replaceAll(' ', '');
      return `${trimmed.substring(0, 3)} ${trimmed.substring(3, 5)}`; // ex: 12345 -> 123 45
    }
    default: {
      return postalCode;
    }
  }
};

export const useShippingProvider = () => {
  const { $shippingMethods, $carts, $me } = useBFF();
  const { isUserLoggedIn } = useUser();
  const error = reactive({
    save: false,
    load: false,
  });
  // const {
  //   load, state, setState,
  // } = useVSFShippingProvider();
  const { send } = useUiNotification();
  const { cart, refresh, setCart } = useCart();
  const { trackError } = useTracking();
  const { $flags } = useNuxtApp();

  const loadingByCountry = ref(false);
  const shippingMethodsByCountry = shallowRef<ShippingMethod[]>();

  const loadingByCart = ref(false);
  const shippingMethodsByCart = shallowRef<ShippingMethod[]>();

  const loadShippingMethodByCountry = async () => {
    loadingByCountry.value = true;
    try {
      const { data } = await $shippingMethods.getShippingMethodsByCountry();
      shippingMethodsByCountry.value = data;
    }
    catch {
      trackError('shipping methods', 'not able to fetch shipping methods for country');
    }
    finally {
      loadingByCountry.value = false;
    }
  };

  const loadShippingMethodByCart = async () => {
    if (shippingMethodsByCart.value) {
      return;
    }

    if (!cart.value) {
      await refresh();
    }

    error.load = false;
    loadingByCart.value = true;
    try {
      const isLoggedIn = await isUserLoggedIn();
      const { data } = await (
        isLoggedIn ? $me.getShippingMethodsByCart(cart.value?.id) : $carts.getShippingMethodsByCart(cart.value?.id)
      );

      shippingMethodsByCart.value = data?.filter(({ key }) => (key === 'PL_INPOST' && $flags.hasnt('shipping.method.inpost')) ? false : true);
    }
    catch {
      error.load = true;
      trackError('shipping methods', 'not able to fetch shipping methods for cart');
      send({
        message: 'There was some error while trying to fetch shipping methods. We are sorry, please try with different shipping details or later.',
        type: 'danger',
      });
    }
    finally {
      loadingByCart.value = false;
    }
  };

  const save = async (shippingMethod: ShippingMethod, address?: Address) => {
    const { id, version } = cart.value || {};

    if (!id || !version) {
      return;
    }

    updating.value = true;
    error.save = false;

    const parsedZipCode = parseZipCode(address?.country, address?.postalCode);

    try {
      const { data } = await $carts.saveShippingMethod(id, version, {
        ...(address ? { address: { ...address, postalCode: parsedZipCode } } : {}),
        shippingMethodId: shippingMethod.id,
      });

      if (data?.cart) {
        await setCart(data.cart);
      }
    }
    catch {
      error.save = true;
      const message = 'There was some error while trying to select this shipping method. We are sorry, please try with different shipping method or later.';
      trackError('shipping methods', message);
      send({
        message,
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  return {
    // setState,
    // state,
    // load,
    save,
    updating: computed(() => updating.value),
    error,
    loadingByCountry: computed(() => loadingByCountry.value),
    loadShippingMethodByCountry,
    loadShippingMethodByCart,
    loadingByCart: computed(() => loadingByCart.value),
    shippingMethodsByCountry: computed(() => shippingMethodsByCountry.value),
    shippingMethodsByCart: computed(() => shippingMethodsByCart.value),
  };
};
